/**
 * booking-fields.js — Single source of truth for every field on the
 * booking form.
 *
 * Introduced during refactor R-7 (2026-07-14) to end the class of
 * "forgot to add the new column to one of three places" drift bugs
 * (driver_tba, broker_id, ...). Historically the same field's default,
 * coercion, and hydration lived in three different files:
 *
 *   1. /lib/constants.js         → bookingDefaults()      (form defaults)
 *   2. /lib/api/bookings/payload.js → buildBookingPayload (form → DB)
 *   3. /components/booking-form/hooks/hydration.js → buildInitialValues (DB → form)
 *
 * Adding a column to `lbs_bookings` but only updating one of those
 * three would silently drop the value on every save/edit. The
 * `driver_tba` bug (BK260703-FE1F5F, July 2026) is the canonical
 * example — cost 4 rounds of debugging.
 *
 * From now on, every form field is declared **once** in this file with
 * its default, coercion, and hydration rules. The three consumer files
 * derive their behaviour from these declarations, so adding a new
 * column requires one edit to one Map — impossible to forget the
 * defaults or hydration in the other two files.
 *
 * Fields intentionally NOT modelled here:
 *   • booking_date / start_time / end_time / _booking_tz
 *       computed at runtime from now() (defaults) or decomposed from
 *       scheduled_start/end (hydration); handled explicitly in each
 *       consumer because the composition/decomposition is site-tz aware.
 *   • site_rules_accepted
 *       form-only gate for the Confirm-and-save button; never written
 *       to the DB payload. Kept as an explicit field in bookingDefaults.
 */

/* ─────────────────────────────────────────────────────────────────────
 * Coercion presets — convert a form value → DB payload value.
 * Every preset assumes the surrounding `nullEmpty()` wrapper will
 * collapse trailing `""` to `null`, so a "str" passthrough is enough
 * for the common "empty string → null column" pattern.
 * ───────────────────────────────────────────────────────────────────── */
const COERCE = {
  /** Passthrough. nullEmpty() collapses "" → null downstream. */
  str:  (v) => v,
  /** Coerce empty string to explicit null (for optional-string columns). */
  orNull: (v) => v || null,
  /** Parse as integer; empty string → null. */
  int:  (v) => (v !== "" && v !== null && v !== undefined ? parseInt(v, 10) : null),
  /** Parse as float; empty string → null. */
  num:  (v) => (v !== "" && v !== null && v !== undefined ? Number(v) : null),
  /** Booleanise. */
  bool: (v) => !!v,
  /** Array passthrough — always an array, never null. */
  arr:  (v) => (Array.isArray(v) ? v : []),
  /** Array OR null if empty (used by food_safety / dangerous_goods
   *  because the DB column allows NULL to mean "not applicable"). */
  arrOrNull: (v) => ((v || []).length ? v : null),
  /** Object OR empty object (used by qa_confirmations JSONB). */
  objOrEmpty: (v) => v || {},
  /** Trailer-clean is "true unless explicitly false" — preserves
   *  the current payload.js semantics that a missing value defaults
   *  to true (bookers must actively confirm dirty). */
  boolDefaultTrue: (v) => v !== false,
};

/* ─────────────────────────────────────────────────────────────────────
 * Hydration presets — convert a DB row → form value.
 * ───────────────────────────────────────────────────────────────────── */
const HYDRATE = {
  /** String, "" if null/undefined. */
  str:  (v) => v || "",
  /** Numeric nullable → "" if null. Preserves 0. */
  num:  (v) => (v ?? ""),
  /** Booleanise. */
  bool: (v) => !!v,
  /** Array, [] if null. */
  arr:  (v) => v || [],
  /** Object, {} if null. */
  obj:  (v) => v || {},
  /** trailer_clean_confirmed: null/undefined → true (schema default). */
  boolDefaultTrue: (v) => v !== false,
};

/* ─────────────────────────────────────────────────────────────────────
 * ROOT_FIELDS — flat columns on lbs_bookings.
 *
 * Order mirrors the section groupings of `bookingDefaults()` for
 * reviewability. Every entry declares:
 *   key       — form value key === DB column name (root fields only)
 *   default   — value used by bookingDefaults()
 *   coerce    — form → DB coercion (see COERCE presets above)
 *   hydrate   — DB → form hydration (see HYDRATE presets above)
 * ───────────────────────────────────────────────────────────────────── */
export const ROOT_FIELDS = [
  // Schedule
  { key: "site_id",              default: "",         coerce: "str",  hydrate: "str"  },
  { key: "bay_id",               default: "",         coerce: "str",  hydrate: "str"  },
  { key: "direction",            default: "inbound",  coerce: "str",
    // direction has an explicit default so a null DB value rehydrates as "inbound"
    hydrate: (v) => v || "inbound" },
  { key: "reservation_type_id",  default: "",         coerce: "orNull", hydrate: "str" },

  // Identity / parties
  { key: "title",                default: "",         coerce: "str",  hydrate: "str"  },
  { key: "description",          default: "",         coerce: "str",  hydrate: "str"  },
  { key: "other_party_name",     default: "",         coerce: "str",  hydrate: "str"  },
  { key: "other_party_contact",  default: "",         coerce: "str",  hydrate: "str"  },

  // Vehicle (flat root columns; JSONB regos handled below)
  { key: "vehicle_type",         default: "",         coerce: "str",  hydrate: "str"  },

  // Load restraint booleans + explicit counts (flat columns on lbs_bookings).
  // The JSONB `load_restraint` parent bundles these plus method — see LOAD_RESTRAINT_FIELDS.
  { key: "straps_count",          default: "", coerce: "int", hydrate: "num" },
  { key: "chains_count",          default: "", coerce: "int", hydrate: "num" },
  { key: "boards_sticks_count",   default: "", coerce: "int", hydrate: "num" },
  { key: "other_restraint_type",  default: "", coerce: "orNull", hydrate: "str" },
  { key: "other_restraint_count", default: "", coerce: "int", hydrate: "num" },

  // Driver licence
  { key: "driver_licence_type",   default: "", coerce: "orNull", hydrate: "str" },
  { key: "driver_licence_number", default: "", coerce: "orNull", hydrate: "str" },
  { key: "driver_licence_state",  default: "", coerce: "orNull", hydrate: "str" },

  // QA confirmations + pallet accounting
  { key: "qa_confirmations",      default: () => ({}), coerce: "objOrEmpty", hydrate: "obj" },
  { key: "pallet_account_in",     default: "", coerce: "orNull", hydrate: "str" },
  { key: "pallet_account_out",    default: "", coerce: "orNull", hydrate: "str" },
  { key: "pallet_docket_number",  default: "", coerce: "orNull", hydrate: "str" },

  // Hold workflow
  { key: "on_hold",     default: false, coerce: "bool", hydrate: "bool" },
  { key: "hold_reason", default: "",    coerce: "orNull", hydrate: "str" },

  // Documents
  { key: "po_number",      default: "", coerce: "str", hydrate: "str" },
  { key: "invoice_number", default: "", coerce: "str", hydrate: "str" },
  { key: "manifest_number", default: "", coerce: "str", hydrate: "str" },

  // External / internal references
  { key: "external_ref",   default: "", coerce: "str", hydrate: "str" },
  { key: "internal_ref",   default: "", coerce: "str", hydrate: "str" },

  // Goods / parties
  { key: "sender_name",      default: "", coerce: "str", hydrate: "str" },
  { key: "sender_company",   default: "", coerce: "str", hydrate: "str" },
  { key: "sender_phone",     default: "", coerce: "str", hydrate: "str" },
  { key: "sender_email",     default: "", coerce: "str", hydrate: "str" },
  { key: "receiver_name",    default: "", coerce: "str", hydrate: "str" },
  { key: "receiver_company", default: "", coerce: "str", hydrate: "str" },
  { key: "goods_description", default: "", coerce: "str", hydrate: "str" },
  { key: "quantity",           default: "", coerce: "int", hydrate: "num" },
  { key: "pallets_in",         default: "", coerce: "int", hydrate: "num" },
  { key: "pallets_out",        default: "", coerce: "int", hydrate: "num" },
  { key: "actual_pallets_in",  default: "", coerce: "int", hydrate: "num" },
  { key: "actual_pallets_out", default: "", coerce: "int", hydrate: "num" },
  { key: "weight",             default: "", coerce: "num", hydrate: "num" },
  { key: "volume",             default: "", coerce: "num", hydrate: "num" },
  { key: "pallet_type",        default: "", coerce: "orNull", hydrate: "str" },

  // Compliance
  { key: "cold_chain",       default: "",     coerce: "str",       hydrate: "str"  },
  { key: "food_safety",      default: () => [], coerce: "arrOrNull", hydrate: "arr" },
  { key: "dangerous_goods",  default: () => [], coerce: "arrOrNull", hydrate: "arr" },
  { key: "compliance_notes", default: "",     coerce: "str",       hydrate: "str"  },

  // Notes
  { key: "notes",            default: "",    coerce: "str",  hydrate: "str"  },

  // R37 / QA
  { key: "forklift_driver_user_id", default: "", coerce: "orNull", hydrate: "str" },
  { key: "temp_set_confirmed",      default: false, coerce: "bool", hydrate: "bool" },
  { key: "load_rule",               default: "",    coerce: "orNull", hydrate: "str" },

  // R37 v8 gap fields (065 + 071)
  { key: "contamination_present",   default: false, coerce: "bool", hydrate: "bool" },
  { key: "contamination_types",     default: () => [], coerce: "arr", hydrate: "arr" },
  { key: "trailer_clean_confirmed", default: true,  coerce: "boolDefaultTrue", hydrate: "boolDefaultTrue" },
  { key: "special_requirements",    default: "", coerce: "orNull", hydrate: "str" },
  { key: "pallet_temp_between_cartons_c", default: "", coerce: "num", hydrate: "num" },
  { key: "sample_pallet1_temp_c",   default: "", coerce: "num", hydrate: "num" },
  { key: "sample_pallet2_temp_c",   default: "", coerce: "num", hydrate: "num" },
  { key: "damage_pallets",          default: false, coerce: "bool", hydrate: "bool" },
  { key: "damage_products",         default: false, coerce: "bool", hydrate: "bool" },
  { key: "damage_notes",            default: "", coerce: "orNull", hydrate: "str" },
  { key: "compliance_temp_confirmed", default: false, coerce: "bool", hydrate: "bool" },

  // Phase E — Approved Booker + assigned driver
  { key: "assigned_driver_id", default: "", coerce: "orNull", hydrate: "str" },
  { key: "approved_booker_id", default: "", coerce: "orNull", hydrate: "str" },

  // Phase 9.3 — Driver TBA flag
  { key: "driver_tba",         default: false, coerce: "bool", hydrate: "bool" },
];

/* ─────────────────────────────────────────────────────────────────────
 * JSONB grouped fields — the form flattens these into individual
 * inputs but the DB stores them as a JSONB parent column.
 *
 * Each entry:
 *   form   — key on the flat form values object
 *   jsonb  — key inside the parent JSONB
 *   default — form default
 *   coerce  — form → JSONB value
 *   hydrate — JSONB → form (defaults to `booking.<parent>?.<jsonb>` lookup)
 * ───────────────────────────────────────────────────────────────────── */
export const VEHICLE_REGOS_FIELDS = [
  { form: "rego_prime_mover", jsonb: "prime_mover", default: "", coerce: "str", hydrate: "str" },
  { form: "rego_trailer_1",   jsonb: "trailer_1",   default: "", coerce: "str", hydrate: "str" },
  { form: "rego_trailer_2",   jsonb: "trailer_2",   default: "", coerce: "str", hydrate: "str" },
  { form: "rego_trailer_3",   jsonb: "trailer_3",   default: "", coerce: "str", hydrate: "str" },
];

export const MASS_LIMITS_FIELDS = [
  { form: "gvm_kg",           jsonb: "gvm_kg",           default: "", coerce: "num",    hydrate: "num" },
  { form: "steer_axle_kg",    jsonb: "steer_axle_kg",    default: "", coerce: "num",    hydrate: "num" },
  { form: "drive_axle_kg",    jsonb: "drive_axle_kg",    default: "", coerce: "num",    hydrate: "num" },
  { form: "axle_group_notes", jsonb: "axle_group_notes", default: "", coerce: "orNull", hydrate: "str" },
];

export const LOAD_RESTRAINT_FIELDS = [
  { form: "restraint_method", jsonb: "method",       default: "",    coerce: "orNull", hydrate: "str"  },
  { form: "has_curtains",     jsonb: "has_curtains", default: false, coerce: "bool",   hydrate: "bool" },
  { form: "has_gates",        jsonb: "has_gates",    default: false, coerce: "bool",   hydrate: "bool" },
  { form: "has_straps",       jsonb: "has_straps",   default: false, coerce: "bool",   hydrate: "bool" },
  { form: "has_chains",       jsonb: "has_chains",   default: false, coerce: "bool",   hydrate: "bool" },
];

export const TRAILER_WEIGHTS_FIELDS = [
  { form: "weight_load_with_pallets", jsonb: "load_with_pallets", default: "", coerce: "num", hydrate: "num" },
  { form: "weight_trailer_1",         jsonb: "trailer_1",         default: "", coerce: "num", hydrate: "num" },
  { form: "weight_trailer_2",         jsonb: "trailer_2",         default: "", coerce: "num", hydrate: "num" },
];

/* ─────────────────────────────────────────────────────────────────────
 * Helpers — the three consumer files (constants / payload / hydration)
 * derive their behaviour from these. Do NOT reimplement — extend here.
 * ───────────────────────────────────────────────────────────────────── */

const applyCoerce = (preset, v) => {
  const fn = typeof preset === "function" ? preset : COERCE[preset];
  if (!fn) throw new Error(`booking-fields: unknown coerce preset "${preset}"`);
  return fn(v);
};

const applyHydrate = (preset, v) => {
  const fn = typeof preset === "function" ? preset : HYDRATE[preset];
  if (!fn) throw new Error(`booking-fields: unknown hydrate preset "${preset}"`);
  return fn(v);
};

const defaultOf = (entry) =>
  typeof entry.default === "function" ? entry.default() : entry.default;

/** Flat defaults for every form field declared in this file. */
export function deriveDefaults() {
  const out = {};
  for (const f of ROOT_FIELDS) out[f.key] = defaultOf(f);
  for (const f of VEHICLE_REGOS_FIELDS) out[f.form] = defaultOf(f);
  for (const f of MASS_LIMITS_FIELDS)   out[f.form] = defaultOf(f);
  for (const f of LOAD_RESTRAINT_FIELDS) out[f.form] = defaultOf(f);
  for (const f of TRAILER_WEIGHTS_FIELDS) out[f.form] = defaultOf(f);
  return out;
}

/**
 * form → DB payload subset covered by the map. Callers merge this
 * with the scheduled_* / status / reference_number values they compute
 * themselves. Result includes:
 *   • flat root-column entries
 *   • the four JSONB parents (vehicle_regos, mass_limits,
 *     load_restraint, trailer_weights) already composed from their
 *     child form fields
 */
export function deriveCoercion(v) {
  const out = {};
  for (const f of ROOT_FIELDS) out[f.key] = applyCoerce(f.coerce, v[f.key]);

  const vehicle_regos = {};
  for (const f of VEHICLE_REGOS_FIELDS)
    vehicle_regos[f.jsonb] = applyCoerce(f.coerce, v[f.form]);
  out.vehicle_regos = vehicle_regos;

  const mass_limits = {};
  for (const f of MASS_LIMITS_FIELDS)
    mass_limits[f.jsonb] = applyCoerce(f.coerce, v[f.form]);
  out.mass_limits = mass_limits;

  const load_restraint = {};
  for (const f of LOAD_RESTRAINT_FIELDS)
    load_restraint[f.jsonb] = applyCoerce(f.coerce, v[f.form]);
  out.load_restraint = load_restraint;

  const trailer_weights = {};
  for (const f of TRAILER_WEIGHTS_FIELDS)
    trailer_weights[f.jsonb] = applyCoerce(f.coerce, v[f.form]);
  out.trailer_weights = trailer_weights;

  return out;
}

/** DB row → flat form values covered by the map. */
export function deriveHydration(booking) {
  const out = {};
  for (const f of ROOT_FIELDS)
    out[f.key] = applyHydrate(f.hydrate, booking[f.key]);

  const vr = booking?.vehicle_regos || {};
  for (const f of VEHICLE_REGOS_FIELDS)
    out[f.form] = applyHydrate(f.hydrate, vr[f.jsonb]);

  const ml = booking?.mass_limits || {};
  for (const f of MASS_LIMITS_FIELDS)
    out[f.form] = applyHydrate(f.hydrate, ml[f.jsonb]);

  const lr = booking?.load_restraint || {};
  for (const f of LOAD_RESTRAINT_FIELDS)
    out[f.form] = applyHydrate(f.hydrate, lr[f.jsonb]);

  const tw = booking?.trailer_weights || {};
  for (const f of TRAILER_WEIGHTS_FIELDS)
    out[f.form] = applyHydrate(f.hydrate, tw[f.jsonb]);

  return out;
}

/** Every DB column that the Map already covers (root + JSONB parents).
 *  Used by the hydration.js guardrail to shrink KNOWN_SKIP — anything
 *  in this set is intrinsically hydrated, no explicit skip needed. */
export const MAP_COVERED_DB_COLUMNS = new Set([
  ...ROOT_FIELDS.map((f) => f.key),
  // JSONB parents (children live inside these; guardrail sees only the parent)
  "vehicle_regos", "mass_limits", "load_restraint", "trailer_weights",
]);
